import React, { useState } from 'react';

function MandatoryDisclosureEditor({ pageContent, setPageContent, uploading, uploadingField, addTableRow, deleteTableRow, updateTableRow }) {
  
  const handleFileUpload = async (itemId, file, section, subSection = null) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      alert('❌ Only PDF, Images (JPG/PNG), and Word documents are allowed.');
      return;
    }
    
    try {
      const { uploadFile } = await import('../../firebase/firebase-config');
      const fileData = await uploadFile(file, `disclosure/${section}_${itemId}_${Date.now()}_${file.name}`);
      
      const updateSection = (items) => items.map(item => 
        item.id === itemId ? { ...item, file: fileData, fileName: file.name } : item
      );
      
      if (section === 'documents') {
        setPageContent({ ...pageContent, documents: updateSection(pageContent.documents || []) });
      } else if (section === 'resultDocuments') {
        setPageContent({ ...pageContent, resultDocuments: updateSection(pageContent.resultDocuments || []) });
      } else if (section === 'staff') {
        setPageContent({ ...pageContent, staff: updateSection(pageContent.staff || []) });
      } else if (section === 'infrastructure') {
        setPageContent({ ...pageContent, infrastructure: updateSection(pageContent.infrastructure || []) });
      }
      alert('✅ File uploaded successfully!');
    } catch (error) {
      alert('❌ Error uploading file: ' + error.message);
    }
  };

  const handleDeleteFile = async (itemId, fileUrl, section) => {
    if (!fileUrl) return;
    
    if (window.confirm('Delete this document permanently?')) {
      try {
        const { deleteFileFromStorage } = await import('../../firebase/firebase-config');
        await deleteFileFromStorage(fileUrl);
        
        const updateSection = (items) => items.map(item => 
          item.id === itemId ? { ...item, file: null, fileName: null } : item
        );
        
        if (section === 'documents') {
          setPageContent({ ...pageContent, documents: updateSection(pageContent.documents || []) });
        } else if (section === 'resultDocuments') {
          setPageContent({ ...pageContent, resultDocuments: updateSection(pageContent.resultDocuments || []) });
        } else if (section === 'staff') {
          setPageContent({ ...pageContent, staff: updateSection(pageContent.staff || []) });
        } else if (section === 'infrastructure') {
          setPageContent({ ...pageContent, infrastructure: updateSection(pageContent.infrastructure || []) });
        }
        alert('✅ File deleted successfully!');
      } catch (error) {
        alert('❌ Error deleting file: ' + error.message);
      }
    }
  };

  const handleReplaceFile = async (itemId, oldFileUrl, section) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    fileInput.onchange = async (e) => {
      if (e.target.files[0]) {
        if (oldFileUrl) {
          try {
            const { deleteFileFromStorage } = await import('../../firebase/firebase-config');
            await deleteFileFromStorage(oldFileUrl);
          } catch (error) {
            console.error('Error deleting old file:', error);
          }
        }
        await handleFileUpload(itemId, e.target.files[0], section);
      }
    };
    fileInput.click();
  };

  const handleYouTubeLinkUpdate = (itemId, youtubeLink, section) => {
    const updateSection = (items) => items.map(item =>
      item.id === itemId ? { ...item, youtubeLink } : item
    );
    
    if (section === 'infrastructure') {
      setPageContent({ ...pageContent, infrastructure: updateSection(pageContent.infrastructure || []) });
    }
  };

  const updateGeneralInfo = (id, field, value) => {
    const newInfo = (pageContent.generalInfo || []).map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setPageContent({ ...pageContent, generalInfo: newInfo });
  };

  const updateResultTable = (id, field, value, tableName) => {
    const newTable = (pageContent[tableName] || []).map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setPageContent({ ...pageContent, [tableName]: newTable });
  };

  const updateStaffRow = (id, field, value) => {
    const newStaff = (pageContent.staff || []).map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setPageContent({ ...pageContent, staff: newStaff });
  };

  const updateInfrastructureRow = (id, field, value) => {
    const newInfra = (pageContent.infrastructure || []).map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setPageContent({ ...pageContent, infrastructure: newInfra });
  };

  const updateTitle = (title) => {
    setPageContent({ ...pageContent, title });
  };

  const deleteRow = (id, section) => {
    if (window.confirm('Delete this row permanently?')) {
      const item = (pageContent[section] || []).find(i => i.id === id);
      if (item?.file?.url) {
        (async () => {
          try {
            const { deleteFileFromStorage } = await import('../../firebase/firebase-config');
            await deleteFileFromStorage(item.file.url);
          } catch (error) {
            console.error('Error deleting file:', error);
          }
        })();
      }
      
      const newItems = (pageContent[section] || []).filter(item => item.id !== id);
      setPageContent({ ...pageContent, [section]: newItems });
    }
  };

  return (
    <div className="page-editor">
      <div className="editor-card">
        <div className="editor-header">
          <h3>⚖️ <strong>CBSE Mandatory Disclosure Editor</strong></h3>
        </div>
        
        <div className="title-editor-section">
          <label className="form-label"><strong>Page Title:</strong></label>
          <input 
            type="text" 
            className="title-input"
            value={pageContent.title || 'CBSE MANDATORY DISCLOSURE'} 
            onChange={(e) => updateTitle(e.target.value)}
            placeholder="Enter page title"
          />
          <p className="field-hint">This title appears at the top of the public page</p>
        </div>

        <p className="editor-description">Add or remove mandatory disclosure information for CBSE compliance. <strong>Empty rows will not be shown</strong> on the public page.</p>

        {/* Section A - General Information */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>A - GENERAL INFORMATION</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('generalInfo', { label: '', value: '' })}>
              + Add Row
            </button>
          </div>
          
          <div className="info-table">
            <div className="table-header">
              <div className="header-label"><strong>Information</strong></div>
              <div className="header-value"><strong>Details</strong></div>
              <div className="header-actions"><strong>Actions</strong></div>
            </div>
            {(pageContent.generalInfo || []).map((item) => (
              <div key={item.id} className="table-row">
                <input 
                  placeholder="e.g., Name of School" 
                  value={item.label || ''} 
                  onChange={(e) => updateGeneralInfo(item.id, 'label', e.target.value)} 
                  className="form-input info-input" 
                />
                <input 
                  placeholder="Details" 
                  value={item.value || ''} 
                  onChange={(e) => updateGeneralInfo(item.id, 'value', e.target.value)} 
                  className="form-input value-input" 
                />
                <button className="remove-btn" onClick={() => deleteRow(item.id, 'generalInfo')}>🗑️ Delete</button>
              </div>
            ))}
          </div>
        </div>

        {/* Section B - Documents / Certificates */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>B - DOCUMENTS / CERTIFICATES</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('documents', { label: '', file: null, fileName: null })}>
              + Add Document
            </button>
          </div>
          
          {(pageContent.documents || []).map((doc) => (
            <div key={doc.id} className="document-item">
              <div className="document-header">
                <input 
                  placeholder="Document Name (e.g., Society Registration Certificate)" 
                  value={doc.label || ''} 
                  onChange={(e) => updateTableRow('documents', doc.id, 'label', e.target.value)} 
                  className="form-input doc-name-input" 
                />
                <button className="remove-btn" onClick={() => deleteRow(doc.id, 'documents')}>🗑️ Delete Row</button>
              </div>
              
              {doc.file && doc.file.url && (
                <div className="file-info">
                  <div className="file-preview">
                    {doc.file.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img src={doc.file.url} alt={doc.label} className="file-preview-image" />
                    ) : <div className="file-icon">📄</div>}
                    <a href={doc.file.url} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a>
                  </div>
                  <div className="file-buttons">
                    <button className="replace-btn" onClick={() => handleReplaceFile(doc.id, doc.file.url, 'documents')}>🔄 Replace</button>
                    <button className="remove-btn-small" onClick={() => handleDeleteFile(doc.id, doc.file.url, 'documents')}>🗑️ Delete File</button>
                  </div>
                </div>
              )}
              
              {(!doc.file || !doc.file.url) && (
                <div className="upload-area">
                  <label className="upload-label">
                    {uploading && uploadingField === `file_${doc.id}` ? '⏳ Uploading...' : '📎 Upload Document'}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display: 'none'}} 
                      onChange={(e) => { if (e.target.files[0]) handleFileUpload(doc.id, e.target.files[0], 'documents'); }} />
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Section C1 - Result and Academics Documents */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>C1 - RESULT AND ACADEMICS DOCUMENTS</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('resultDocuments', { label: '', file: null, fileName: null })}>
              + Add Document
            </button>
          </div>
          
          {(pageContent.resultDocuments || []).map((doc) => (
            <div key={doc.id} className="document-item">
              <div className="document-header">
                <input 
                  placeholder="Document Name (e.g., Result Notification)" 
                  value={doc.label || ''} 
                  onChange={(e) => updateTableRow('resultDocuments', doc.id, 'label', e.target.value)} 
                  className="form-input doc-name-input" 
                />
                <button className="remove-btn" onClick={() => deleteRow(doc.id, 'resultDocuments')}>🗑️ Delete Row</button>
              </div>
              
              {doc.file && doc.file.url && (
                <div className="file-info">
                  <div className="file-preview">
                    {doc.file.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img src={doc.file.url} alt={doc.label} className="file-preview-image" />
                    ) : <div className="file-icon">📄</div>}
                    <a href={doc.file.url} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a>
                  </div>
                  <div className="file-buttons">
                    <button className="replace-btn" onClick={() => handleReplaceFile(doc.id, doc.file.url, 'resultDocuments')}>🔄 Replace</button>
                    <button className="remove-btn-small" onClick={() => handleDeleteFile(doc.id, doc.file.url, 'resultDocuments')}>🗑️ Delete File</button>
                  </div>
                </div>
              )}
              
              {(!doc.file || !doc.file.url) && (
                <div className="upload-area">
                  <label className="upload-label">
                    {uploading && uploadingField === `resultDoc_${doc.id}` ? '⏳ Uploading...' : '📎 Upload Document'}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display: 'none'}} 
                      onChange={(e) => { if (e.target.files[0]) handleFileUpload(doc.id, e.target.files[0], 'resultDocuments'); }} />
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Section C2 - Result Class X Table */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>C2 - RESULT CLASS X</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('resultClassX', { sNo: '', year: '', registered: '', passed: '', percentage: '', remarks: '' })}>
              + Add Row
            </button>
          </div>
          
          <div className="result-table-wrapper">
            <table className="result-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Year</th>
                  <th>No. of Registered Students</th>
                  <th>No. of Students Passed</th>
                  <th>Pass Percentage</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(pageContent.resultClassX || []).map((row) => (
                  <tr key={row.id}>
                    <td><input value={row.sNo || ''} onChange={(e) => updateResultTable(row.id, 'sNo', e.target.value, 'resultClassX')} placeholder="1" /></td>
                    <td><input value={row.year || ''} onChange={(e) => updateResultTable(row.id, 'year', e.target.value, 'resultClassX')} placeholder="2024" /></td>
                    <td><input value={row.registered || ''} onChange={(e) => updateResultTable(row.id, 'registered', e.target.value, 'resultClassX')} placeholder="100" /></td>
                    <td><input value={row.passed || ''} onChange={(e) => updateResultTable(row.id, 'passed', e.target.value, 'resultClassX')} placeholder="98" /></td>
                    <td><input value={row.percentage || ''} onChange={(e) => updateResultTable(row.id, 'percentage', e.target.value, 'resultClassX')} placeholder="98%" /></td>
                    <td><input value={row.remarks || ''} onChange={(e) => updateResultTable(row.id, 'remarks', e.target.value, 'resultClassX')} placeholder="Excellent" /></td>
                    <td><button className="remove-btn-small" onClick={() => deleteRow(row.id, 'resultClassX')}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section C3 - Result Class XII Table */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>C3 - RESULT CLASS XII</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('resultClassXII', { sNo: '', year: '', registered: '', passed: '', percentage: '', remarks: '' })}>
              + Add Row
            </button>
          </div>
          
          <div className="result-table-wrapper">
            <table className="result-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Year</th>
                  <th>No. of Registered Students</th>
                  <th>No. of Students Passed</th>
                  <th>Pass Percentage</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(pageContent.resultClassXII || []).map((row) => (
                  <tr key={row.id}>
                    <td><input value={row.sNo || ''} onChange={(e) => updateResultTable(row.id, 'sNo', e.target.value, 'resultClassXII')} placeholder="1" /></td>
                    <td><input value={row.year || ''} onChange={(e) => updateResultTable(row.id, 'year', e.target.value, 'resultClassXII')} placeholder="2024" /></td>
                    <td><input value={row.registered || ''} onChange={(e) => updateResultTable(row.id, 'registered', e.target.value, 'resultClassXII')} placeholder="100" /></td>
                    <td><input value={row.passed || ''} onChange={(e) => updateResultTable(row.id, 'passed', e.target.value, 'resultClassXII')} placeholder="98" /></td>
                    <td><input value={row.percentage || ''} onChange={(e) => updateResultTable(row.id, 'percentage', e.target.value, 'resultClassXII')} placeholder="98%" /></td>
                    <td><input value={row.remarks || ''} onChange={(e) => updateResultTable(row.id, 'remarks', e.target.value, 'resultClassXII')} placeholder="Excellent" /></td>
                    <td><button className="remove-btn-small" onClick={() => deleteRow(row.id, 'resultClassXII')}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section D - Staff (Teaching) */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>D - STAFF (TEACHING)</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('staff', { sNo: '', information: '', number: '', qualifications: '', file: null, fileName: null })}>
              + Add Row
            </button>
          </div>
          
          {(pageContent.staff || []).map((staff) => (
            <div key={staff.id} className="staff-item">
              <div className="staff-header">
                <input value={staff.sNo || ''} onChange={(e) => updateStaffRow(staff.id, 'sNo', e.target.value)} placeholder="S.No" className="staff-sno" />
                <input value={staff.information || ''} onChange={(e) => updateStaffRow(staff.id, 'information', e.target.value)} placeholder="Information (e.g., Total Teachers)" className="staff-info" />
                <input value={staff.number || ''} onChange={(e) => updateStaffRow(staff.id, 'number', e.target.value)} placeholder="Number / Strength" className="staff-number" />
                <button className="remove-btn-small" onClick={() => deleteRow(staff.id, 'staff')}>Delete</button>
              </div>
              <textarea value={staff.qualifications || ''} onChange={(e) => updateStaffRow(staff.id, 'qualifications', e.target.value)} placeholder="Name and Qualifications" className="staff-qualifications" rows="3" />
              
              {staff.file && staff.file.url && (
                <div className="file-info staff-file">
                  <div className="file-preview">
                    {staff.file.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img src={staff.file.url} alt={staff.information} className="file-preview-image" />
                    ) : <div className="file-icon">📄</div>}
                    <a href={staff.file.url} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a>
                  </div>
                  <div className="file-buttons">
                    <button className="replace-btn" onClick={() => handleReplaceFile(staff.id, staff.file.url, 'staff')}>🔄 Replace</button>
                    <button className="remove-btn-small" onClick={() => handleDeleteFile(staff.id, staff.file.url, 'staff')}>🗑️ Delete File</button>
                  </div>
                </div>
              )}
              
              {(!staff.file || !staff.file.url) && (
                <div className="upload-area staff-upload">
                  <label className="upload-label-small">
                    {uploading && uploadingField === `staff_${staff.id}` ? '⏳ Uploading...' : '📎 Upload Supporting Document (Optional)'}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display: 'none'}} 
                      onChange={(e) => { if (e.target.files[0]) handleFileUpload(staff.id, e.target.files[0], 'staff'); }} />
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Section E - School Infrastructure */}
        <div className="form-card">
          <div className="section-header">
            <h4><strong>E - SCHOOL INFRASTRUCTURE</strong></h4>
            <button className="add-btn" onClick={() => addTableRow('infrastructure', { sNo: '', information: '', details: '', file: null, fileName: null, youtubeLink: '' })}>
              + Add Row
            </button>
          </div>
          
          {(pageContent.infrastructure || []).map((item) => (
            <div key={item.id} className="infrastructure-item">
              <div className="infrastructure-header">
                <input value={item.sNo || ''} onChange={(e) => updateInfrastructureRow(item.id, 'sNo', e.target.value)} placeholder="S.No" className="infra-sno" />
                <input value={item.information || ''} onChange={(e) => updateInfrastructureRow(item.id, 'information', e.target.value)} placeholder="Information (e.g., Classrooms)" className="infra-info" />
                <button className="remove-btn-small" onClick={() => deleteRow(item.id, 'infrastructure')}>Delete</button>
              </div>
              <textarea value={item.details || ''} onChange={(e) => updateInfrastructureRow(item.id, 'details', e.target.value)} placeholder="Details (e.g., 20 classrooms with smart boards)" className="infra-details" rows="2" />
              
              <div className="infra-uploads">
                {item.file && item.file.url && (
                  <div className="file-info infra-file">
                    <div className="file-preview">
                      {item.file.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                        <img src={item.file.url} alt={item.information} className="file-preview-image" />
                      ) : <div className="file-icon">📄</div>}
                      <a href={item.file.url} target="_blank" rel="noopener noreferrer" className="file-link">View Document</a>
                    </div>
                    <button className="remove-btn-small" onClick={() => handleDeleteFile(item.id, item.file.url, 'infrastructure')}>🗑️ Delete File</button>
                  </div>
                )}
                
                {(!item.file || !item.file.url) && (
                  <label className="upload-label-small">
                    📎 Upload Document (Optional)
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display: 'none'}} 
                      onChange={(e) => { if (e.target.files[0]) handleFileUpload(item.id, e.target.files[0], 'infrastructure'); }} />
                  </label>
                )}
                
                <div className="youtube-link">
                  <input 
                    value={item.youtubeLink || ''} 
                    onChange={(e) => handleYouTubeLinkUpdate(item.id, e.target.value, 'infrastructure')} 
                    placeholder="YouTube Video Link (Optional)" 
                    className="youtube-input" 
                  />
                  {item.youtubeLink && (
                    <a href={item.youtubeLink} target="_blank" rel="noopener noreferrer" className="youtube-preview-link">▶ Watch Video</a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="info-box">
          <div className="info-icon">💡</div>
          <div className="info-content">
            <strong>How to use this editor:</strong>
            <ul>
              <li><strong>Edit Page Title</strong> - Change the main heading at the top of the page</li>
              <li><strong>Add Row</strong> - Create a new row for any section</li>
              <li><strong>Delete Row</strong> - Remove entire row (file will also be deleted if exists)</li>
              <li><strong>Upload Document</strong> - Support for PDF, Images, Word files</li>
              <li><strong>YouTube Link</strong> - Add virtual tour or infrastructure videos (Section E)</li>
              <li><strong>Empty rows will NOT be shown</strong> on the public page</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .editor-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .editor-header h3 {
          margin-top: 0;
          margin-bottom: 20px;
          color: #1a3a6b;
          font-size: 24px;
        }
        .title-editor-section {
          background: #f0f9ff;
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;
          border: 1px solid #bae6fd;
        }
        .form-label {
          display: block;
          margin-bottom: 8px;
          font-size: 14px;
          color: #1e40af;
        }
        .title-input {
          width: 100%;
          padding: 12px;
          border: 2px solid #93c5fd;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
        }
        .field-hint {
          font-size: 12px;
          color: #6b7280;
          margin-top: 6px;
          margin-bottom: 0;
        }
        .editor-description { 
          color: #64748b; 
          margin-bottom: 20px; 
          font-size: 14px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }
        .form-card { 
          background: #f8fafc; 
          padding: 20px; 
          border-radius: 16px; 
          margin-bottom: 25px; 
          border: 1px solid #e2e8f0; 
        }
        .section-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 20px; 
          flex-wrap: wrap; 
          gap: 10px; 
        }
        .section-header h4 { 
          margin: 0; 
          color: #1a3a6b; 
          font-size: 18px;
          border-left: 4px solid #f59e0b;
          padding-left: 12px;
        }
        .add-btn { 
          background: #10b981; 
          color: white; 
          border: none; 
          padding: 8px 16px; 
          border-radius: 8px; 
          cursor: pointer; 
          font-weight: 600; 
        }
        .remove-btn { 
          background: #ef4444; 
          color: white; 
          border: none; 
          padding: 6px 12px; 
          border-radius: 6px; 
          cursor: pointer; 
          font-size: 12px; 
        }
        .remove-btn-small { 
          background: #ef4444; 
          color: white; 
          border: none; 
          padding: 4px 10px; 
          border-radius: 4px; 
          cursor: pointer; 
          font-size: 11px; 
        }
        .replace-btn { 
          background: #f59e0b; 
          color: white; 
          border: none; 
          padding: 4px 10px; 
          border-radius: 4px; 
          cursor: pointer; 
          font-size: 11px; 
        }
        
        .info-table { width: 100%; }
        .table-header { 
          display: grid; 
          grid-template-columns: 2fr 3fr auto; 
          gap: 12px; 
          margin-bottom: 10px; 
          padding: 10px; 
          background: #e2e8f0; 
          border-radius: 8px; 
          font-size: 13px; 
        }
        .table-row { 
          display: grid; 
          grid-template-columns: 2fr 3fr auto; 
          gap: 12px; 
          margin-bottom: 10px; 
          align-items: center; 
        }
        .form-input { 
          width: 100%; 
          padding: 8px 12px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
        }
        
        .document-item { 
          background: white; 
          border: 1px solid #e2e8f0; 
          border-radius: 12px; 
          padding: 15px; 
          margin-bottom: 15px; 
        }
        .document-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 15px; 
          flex-wrap: wrap; 
          gap: 10px; 
        }
        .doc-name-input { 
          flex: 1; 
          min-width: 200px; 
          padding: 8px 12px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
        }
        
        .file-info { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          padding: 12px; 
          background: #f1f5f9; 
          border-radius: 10px; 
          margin-bottom: 12px; 
          flex-wrap: wrap; 
          gap: 12px; 
        }
        .file-preview { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
        }
        .file-preview-image { 
          width: 40px; 
          height: 40px; 
          object-fit: cover; 
          border-radius: 6px; 
        }
        .file-icon { 
          font-size: 28px; 
        }
        .file-link { 
          color: #1a3a6b; 
          text-decoration: none; 
          font-weight: 500; 
          font-size: 13px; 
        }
        .file-buttons { 
          display: flex; 
          gap: 8px; 
        }
        .upload-area { 
          margin-top: 10px; 
        }
        .upload-label, .upload-label-small { 
          background: #3b82f6; 
          color: white; 
          padding: 8px 16px; 
          border-radius: 6px; 
          cursor: pointer; 
          font-size: 13px; 
          display: inline-block; 
        }
        .upload-label-small { 
          background: #64748b; 
          padding: 6px 12px; 
          font-size: 12px; 
        }
        
        .result-table-wrapper { 
          overflow-x: auto; 
        }
        .result-table { 
          width: 100%; 
          border-collapse: collapse; 
          background: white; 
          border-radius: 8px; 
          overflow: hidden; 
        }
        .result-table th, .result-table td { 
          border: 1px solid #e2e8f0; 
          padding: 10px; 
          text-align: left; 
          font-size: 13px; 
        }
        .result-table th { 
          background: #1a3a6b; 
          color: white; 
          font-weight: 600; 
        }
        .result-table input { 
          width: 100%; 
          padding: 6px; 
          border: 1px solid #cbd5e1; 
          border-radius: 4px; 
          font-size: 12px; 
        }
        
        .staff-item { 
          background: white; 
          border: 1px solid #e2e8f0; 
          border-radius: 12px; 
          padding: 15px; 
          margin-bottom: 15px; 
        }
        .staff-header { 
          display: grid; 
          grid-template-columns: 60px 1fr 100px auto; 
          gap: 10px; 
          margin-bottom: 12px; 
          align-items: center; 
        }
        .staff-sno, .staff-info, .staff-number { 
          padding: 8px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
        }
        .staff-qualifications { 
          width: 100%; 
          padding: 8px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
          margin-bottom: 12px; 
        }
        .staff-file { 
          margin-top: 12px; 
        }
        .staff-upload { 
          margin-top: 12px; 
        }
        
        .infrastructure-item { 
          background: white; 
          border: 1px solid #e2e8f0; 
          border-radius: 12px; 
          padding: 15px; 
          margin-bottom: 15px; 
        }
        .infrastructure-header { 
          display: grid; 
          grid-template-columns: 60px 1fr auto; 
          gap: 10px; 
          margin-bottom: 12px; 
          align-items: center; 
        }
        .infra-sno, .infra-info { 
          padding: 8px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
        }
        .infra-details { 
          width: 100%; 
          padding: 8px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
          margin-bottom: 12px; 
        }
        .infra-uploads { 
          display: flex; 
          flex-direction: column; 
          gap: 12px; 
        }
        .infra-file { 
          margin: 0; 
        }
        .youtube-link { 
          display: flex; 
          gap: 12px; 
          align-items: center; 
          flex-wrap: wrap; 
        }
        .youtube-input { 
          flex: 1; 
          padding: 8px; 
          border: 1px solid #cbd5e1; 
          border-radius: 6px; 
        }
        .youtube-preview-link { 
          color: #ef4444; 
          text-decoration: none; 
          font-weight: 600; 
          font-size: 13px; 
        }
        
        .info-box { 
          background: #eff6ff; 
          border-left: 4px solid #3b82f6; 
          padding: 16px; 
          border-radius: 12px; 
          margin-top: 20px; 
          display: flex; 
          gap: 12px; 
        }
        .info-icon { 
          font-size: 20px; 
        }
        .info-content ul { 
          margin: 8px 0 0 20px; 
          color: #64748b; 
          font-size: 13px; 
        }
        
        @media (max-width: 768px) {
          .table-header, .table-row { 
            grid-template-columns: 1fr; 
            gap: 8px; 
          }
          .table-header { 
            display: none; 
          }
          .staff-header { 
            grid-template-columns: 1fr; 
          }
          .infrastructure-header { 
            grid-template-columns: 1fr; 
          }
          .result-table th, .result-table td { 
            font-size: 11px; 
            padding: 6px; 
          }
        }
      `}</style>
    </div>
  );
}

export default MandatoryDisclosureEditor;